import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Card from '../../ui/Card.jsx';
import InfoTooltip from '../../ui/InfoTooltip.jsx';
import { fetchPersonIntelligence, fetchProjectsList } from '../../api.js';
import {
  PERIOD_OPTIONS,
  clamp01,
  formatHours,
  formatPct,
  initials,
} from './peopleMockData.js';

function useQuery() {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
}

function Chip({ children }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs border"
      style={{
        borderColor: 'var(--ui-border)',
        background: 'var(--ui-surface2)',
        color: 'var(--ui-text)',
      }}
    >
      {children}
    </span>
  );
}

function ProgressBar({ value }) {
  const pct = clamp01(value);
  return (
    <div
      className="h-3 rounded"
      style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }}
    >
      <div
        className="h-3 rounded"
        style={{
          width: `${Math.round(pct * 100)}%`,
          background: 'var(--ui-primary)',
        }}
      />
    </div>
  );
}

function PersonAvatar({ name, avatarUrl }) {
  const hasImg = Boolean(String(avatarUrl || '').trim());
  return (
    <div
      className="h-10 w-10 rounded-full flex items-center justify-center overflow-hidden"
      style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}
    >
      {hasImg ? (
        <img src={avatarUrl} alt={name || 'Avatar'} className="h-10 w-10 object-cover" />
      ) : (
        <span className="text-xs font-semibold" style={{ color: 'var(--ui-text-muted)' }}>
          {initials(name)}
        </span>
      )}
    </div>
  );
}

function fmtDayShortFromYmd(ymd) {
  const d = new Date(`${ymd}T00:00:00`);
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return names[d.getDay()] || '';
}

function fmtMDFromYmd(ymd) {
  const d = new Date(`${ymd}T00:00:00`);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${m}/${dd}`;
}

function isOverdueDate(dueDateYmd, status) {
  if (!dueDateYmd || dueDateYmd === '—') return false;
  if (String(status) === 'gotovo') return false;
  const today = new Date().toISOString().slice(0, 10);
  return String(dueDateYmd) < today;
}

function buildProjectTasksUrl({ projectId, taskId, personId, period, status }) {
  const pid = String(projectId ?? '').trim();
  if (!pid) return null;

  const params = new URLSearchParams();
  params.set('tab', 'tasks');
  if (taskId != null && String(taskId).trim() !== '') params.set('taskId', String(taskId));
  if (personId) params.set('person', String(personId));
  if (period) params.set('period', String(period));
  if (status) params.set('status', String(status));

  const qs = params.toString();
  return `/projects/${encodeURIComponent(pid)}${qs ? `?${qs}` : ''}`;
}

export default function EmployeeIntelligencePage() {
  const navigate = useNavigate();
  const params = useParams();
  const query = useQuery();

  const personId = String(params?.personId || '').trim();
  const initialPeriod = String(query.get('period') || 'thisWeek');
  const initialProject = String(query.get('project') || 'all');

  const [period, setPeriod] = useState(PERIOD_OPTIONS.some((p) => p.key === initialPeriod) ? initialPeriod : 'thisWeek');
  const [projectId, setProjectId] = useState(initialProject || 'all');

  const [projects, setProjects] = useState([]);
  const projectById = useMemo(() => {
    const m = new Map();
    for (const p of (Array.isArray(projects) ? projects : [])) m.set(String(p.id), p);
    return m;
  }, [projects]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchProjectsList();
        if (cancelled) return;
        setProjects(Array.isArray(rows) ? rows : []);
      } catch (_) {
        if (cancelled) return;
        setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useMemo(() => {
    return async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await fetchPersonIntelligence(personId, {
          period,
          projectId: projectId === 'all' ? null : projectId,
        });
        setData(res);
      } catch (err) {
        setData(null);
        setLoadError(err?.message || 'Failed to load person intelligence');
      } finally {
        setLoading(false);
      }
    };
  }, [personId, period, projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => { cancelled = true; };
  }, [load]);

  const person = data?.person || null;
  const summary = data?.summary || null;

  const projectBreakdown = useMemo(() => {
    const planned = Array.isArray(data?.breakdown?.plannedByProject) ? data.breakdown.plannedByProject : [];
    const logged = Array.isArray(data?.breakdown?.loggedByProject) ? data.breakdown.loggedByProject : [];
    const loggedByPid = new Map(logged.map((r) => [String(r.projectId), Number(r.loggedMinutes || 0)]));

    const rows = planned.map((r) => {
      const pid = String(r.projectId);
      const plannedMinutes = Number(r.plannedMinutes || 0);
      const loggedMinutes = Number(loggedByPid.get(pid) || 0);
      const proj = projectById.get(pid);
      const pct = plannedMinutes > 0 ? (loggedMinutes / plannedMinutes) : 0;
      return {
        projectId: Number(r.projectId),
        projectName: proj?.name || `Project ${pid}`,
        planned: plannedMinutes / 60,
        logged: loggedMinutes / 60,
        pct,
        activeTasks: '—',
      };
    });

    return projectId === 'all' ? rows : rows.filter((r) => String(r.projectId) === String(projectId));
  }, [data, projectById, projectId]);

  const tasks = useMemo(() => {
    const rows = Array.isArray(data?.assignments) ? data.assignments : [];
    const mapped = rows.map((a) => {
      const pid = a.projectId;
      const proj = projectById.get(String(pid));
      return {
        id: a.id,
        projectId: pid,
        projectName: a.projectName || proj?.name || (pid != null ? `Project ${pid}` : '—'),
        title: a.taskName || a.jobName || (a.taskId ? `Task ${a.taskId}` : `Assignment ${String(a.id).slice(0, 8)}`),
        dueDate: a.dueDate || '—',
        status: a.status,
        topPriority: !!a.topPriority,
        progress: Number(a.progress || 0),
        plannedHours: Number(a.plannedMinutes || 0) / 60,
        loggedHours: Number(a.loggedMinutes || 0) / 60,
        taskId: a.taskId ?? null,
      };
    });

    return projectId === 'all' ? mapped : mapped.filter((r) => String(r.projectId) === String(projectId));
  }, [data, projectById, projectId]);

  const overdueTasks = useMemo(() => {
    const rows = Array.isArray(data?.overdueAssignments) ? data.overdueAssignments : [];
    return rows.map((a) => {
      const pid = a.projectId;
      const proj = projectById.get(String(pid));
      return {
        id: a.id,
        projectId: pid,
        projectName: a.projectName || proj?.name || (pid != null ? `Project ${pid}` : '—'),
        title: a.taskName || a.jobName || (a.taskId ? `Task ${a.taskId}` : `Assignment ${String(a.id).slice(0, 8)}`),
        dueDate: a.dueDate || '—',
        progress: Number(a.progress || 0),
        topPriority: !!a.topPriority,
        taskId: a.taskId ?? null,
      };
    });
  }, [data, projectById]);

  const overdueProjectIds = useMemo(() => {
    const set = new Set();
    for (const t of Array.isArray(overdueTasks) ? overdueTasks : []) {
      const pid = t?.projectId;
      if (pid == null || String(pid).trim() === '') continue;
      set.add(String(pid));
    }
    return set;
  }, [overdueTasks]);

  const openTasksContextUrl = useMemo(() => {
    // Prefer the currently selected project filter.
    if (projectId !== 'all' && String(projectId).trim()) {
      return buildProjectTasksUrl({ projectId, personId, period });
    }

    // If all tasks are from a single project, open that project.
    const taskProjectIds = new Set();
    for (const t of Array.isArray(tasks) ? tasks : []) {
      const pid = t?.projectId;
      if (pid == null || String(pid).trim() === '') continue;
      taskProjectIds.add(String(pid));
      if (taskProjectIds.size > 1) break;
    }
    if (taskProjectIds.size === 1) {
      const onlyPid = Array.from(taskProjectIds)[0];
      return buildProjectTasksUrl({ projectId: onlyPid, personId, period });
    }

    // No single project context; fall back to Projects list.
    const params = new URLSearchParams();
    if (personId) params.set('person', String(personId));
    if (period) params.set('period', String(period));
    const qs = params.toString();
    return `/projects${qs ? `?${qs}` : ''}`;
  }, [period, personId, projectId, tasks]);

  const week = useMemo(() => {
    const daily = Array.isArray(data?.daily) ? data.daily : [];
    const first5 = daily.slice(0, 5);
    const capPerDay = summary && Number(summary.capacityHours || 0) > 0 ? Number(summary.capacityHours || 0) / 5 : 0;
    return first5.map((d, idx) => {
      const ymd = d.date;
      const plannedHours = Number(d.plannedMinutes || 0) / 60;
      const loggedHours = Number(d.loggedMinutes || 0) / 60;
      const empty = plannedHours <= 0.01 && loggedHours <= 0.01 && !d.isVacation;
      const capacityHoursRaw = d.capacityMinutes != null ? (Number(d.capacityMinutes || 0) / 60) : capPerDay;
      const capacityHours = Math.round(Number(capacityHoursRaw || 0) * 10) / 10;
      return {
        key: `${ymd || 'd'}-${idx}`,
        ymd,
        label: fmtDayShortFromYmd(ymd),
        md: fmtMDFromYmd(ymd),
        plannedHours,
        loggedHours,
        capacityHours,
        empty,
        isVacation: !!d.isVacation,
      };
    });
  }, [data, summary]);

  const weekStats = useMemo(() => {
    let overloadedDays = 0;
    let emptyDays = 0;
    for (const d of Array.isArray(week) ? week : []) {
      const planned = Number(d.plannedHours || 0);
      const logged = Number(d.loggedHours || 0);
      const capacity = Number(d.capacityHours || 0);
      const isVacation = !!d.isVacation;

      const isEmpty = !!d.empty || (planned <= 0.01 && logged <= 0.01 && !isVacation);
      if (isEmpty) emptyDays += 1;

      if (!isVacation) {
        const overloaded = planned > 8 || (capacity > 0 && planned > capacity);
        if (overloaded) overloadedDays += 1;
      }
    }
    return { overloadedDays, emptyDays };
  }, [week]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/people')}
          className="text-sm rounded px-3 py-2 border inline-flex items-center w-fit"
          style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
        >
          Back to People
        </button>
        <Card hover={false}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
            <div className="flex-1">
              <div className="h-4 w-48 rounded" style={{ background: 'var(--ui-surface2)' }} />
              <div className="h-3 w-24 rounded mt-2" style={{ background: 'var(--ui-surface2)' }} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div className="h-20 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
            <div className="h-20 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
            <div className="h-20 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
          </div>
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/people')}
          className="text-sm rounded px-3 py-2 border inline-flex items-center w-fit"
          style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
        >
          Back to People
        </button>
        <Card hover={false}>
          <div className="text-sm" style={{ color: 'var(--ui-text)' }}>Failed to load person intelligence.</div>
          <div className="text-xs mt-1" style={{ color: 'var(--ui-text-muted)' }}>{loadError}</div>
          <button
            type="button"
            onClick={() => load()}
            className="mt-3 text-sm rounded px-3 py-2 border inline-flex items-center w-fit"
            style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
          >
            Retry
          </button>
        </Card>
      </div>
    );
  }

  if (!person || !summary) {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/people')}
          className="text-sm rounded px-3 py-2 border inline-flex items-center w-fit"
          style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
        >
          Back to People
        </button>
        <Card hover={false}>
          <div className="text-sm" style={{ color: 'var(--ui-text)' }}>Person not found.</div>
          <div className="text-xs mt-2" style={{ color: 'var(--ui-text-muted)' }}>ID: {personId || '—'}</div>
        </Card>
      </div>
    );
  }

  const headerBgStyle = {
    background: 'var(--ui-bg)',
    borderBottom: '1px solid var(--ui-border)',
  };

  const utilPct = Math.round((Number(summary.utilization || 0)) * 100);
  const overloadPct = Math.round((Number(summary.overload || 0)) * 100);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="sticky top-0 z-10 py-2" style={headerBgStyle}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/people')}
              className="text-sm rounded px-3 py-2 border inline-flex items-center"
              style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
            >
              Back to People
            </button>
            <div className="flex items-center gap-3 min-w-0">
              <PersonAvatar name={person.name} avatarUrl={person.avatarUrl} />
              <div className="min-w-0">
                <div className="text-lg font-semibold truncate" style={{ color: 'var(--ui-text)' }}>{person.name}</div>
                <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>{person.appRole || person.role}</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-2 py-1 text-sm rounded border"
              style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
              aria-label="Period"
              title="Period"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="px-2 py-1 text-sm rounded border"
              style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
              aria-label="Project"
              title="Project"
            >
              <option value="all">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>

            <button
              type="button"
              className="text-sm rounded px-3 py-2 border"
              style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
              onClick={() => navigate(`/calendar?person=${encodeURIComponent(person.id)}&period=${encodeURIComponent(period)}`)}
            >
              Open Calendar
            </button>
            <button
              type="button"
              className="text-sm rounded px-3 py-2 border"
              style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
              onClick={() => navigate(openTasksContextUrl)}
            >
              Tasks
            </button>
          </div>
        </div>
      </div>

      {/* Week Snapshot (prominent) */}
      <Card hover={false} padded={false}>
        <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>Week Snapshot (Mon–Fri)</div>
              <InfoTooltip
                ariaLabel="Week snapshot info"
                text="Overloaded day: planned hours > 8h OR > that day’s capacity.\nEmpty day: planned = 0h AND logged = 0h (vacation days are not counted as empty)."
                maxWidthClassName="w-96"
              />
            </div>
            <div className="text-xs mt-1" style={{ color: 'var(--ui-text-muted)' }}>
              Overloaded days: <span className="font-semibold" style={{ color: 'var(--ui-text)' }}>{weekStats.overloadedDays}</span> · Empty days:{' '}
              <span className="font-semibold" style={{ color: 'var(--ui-text)' }}>{weekStats.emptyDays}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(`/calendar?person=${encodeURIComponent(person.id)}&period=${encodeURIComponent(period)}`)}
            className="text-xs underline"
            style={{ color: 'var(--ui-primary)' }}
          >
            Open full calendar
          </button>
        </div>

        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
            {week.map((d) => {
              const overloaded = !d.isVacation && (Number(d.plannedHours || 0) > 8 || (Number(d.capacityHours || 0) > 0 && Number(d.plannedHours || 0) > Number(d.capacityHours || 0)));
              const isEmpty = !!d.empty;
              return (
                <div
                  key={d.key}
                  className="rounded p-3 text-center"
                  style={{
                    border: '1px solid var(--ui-border)',
                    background: isEmpty ? 'var(--ui-surface)' : 'var(--ui-surface2)',
                  }}
                >
                  <div className="text-xs font-semibold" style={{ color: 'var(--ui-text)' }}>{d.label}</div>
                  <div className="text-[11px]" style={{ color: 'var(--ui-text-muted)' }}>{d.md}</div>

                  <div className="mt-2 text-sm font-semibold" style={{ color: isEmpty ? 'var(--ui-text-muted)' : (overloaded ? 'var(--ui-danger)' : 'var(--ui-text)') }}>
                    {isEmpty ? '—' : formatHours(d.plannedHours)}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: 'var(--ui-text-muted)' }}>planned</div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* KPI Snapshot */}
      <Card hover={false}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-[240px]">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>KPI Snapshot</div>
              <InfoTooltip
                ariaLabel="KPI snapshot info"
                text="Capacity is workday capacity after subtracting vacation days (vacation reduces available hours)."
                maxWidthClassName="w-96"
              />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Capacity</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{formatHours(summary.capacityHours)}</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Vacation days</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{summary.vacationDays}</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Planned</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{formatHours(summary.plannedHours)}</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Logged</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{formatHours(summary.loggedHours)}</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Utilization %</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{utilPct}%</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Overload %</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{overloadPct}%</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>No-log days</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{Number(summary.noLogDays || 0)}</div>
              </div>
              <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                <div style={{ color: 'var(--ui-text-muted)' }}>Retro-log %</div>
                <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{summary.retroLogRatio == null ? '—' : formatPct(summary.retroLogRatio)}</div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-[260px]">
            <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>Utilization</div>
            <div className="mt-1">
              <ProgressBar value={summary.utilization} />
            </div>

            <div className="mt-3 text-xs" style={{ color: 'var(--ui-text-muted)' }}>Overload</div>
            <div className="mt-1">
              <ProgressBar value={summary.overload} />
            </div>

            <div className="mt-3 flex items-center gap-2">
              <div className="text-xs font-semibold" style={{ color: 'var(--ui-text)' }}>Flags</div>
              <InfoTooltip
                ariaLabel="Flags info"
                text="Over capacity: planned > 110% of capacity.\nNear capacity: planned 100–110%.\nOverdue: has overdue tasks.\nNo logs: at least one workday with 0 logged minutes."
                maxWidthClassName="w-96"
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {summary.flags?.overloaded && <Chip>Over capacity</Chip>}
              {!summary.flags?.overloaded && summary.flags?.nearCapacity && <Chip>Near capacity</Chip>}
              {summary.flags?.overdue && <Chip>{Number(summary.overdueCount || 0)} overdue</Chip>}
              {summary.flags?.noLogs && <Chip>No logs</Chip>}
              {!summary.flags?.overloaded && !summary.flags?.nearCapacity && !summary.flags?.overdue && !summary.flags?.noLogs && <Chip>—</Chip>}
            </div>
          </div>
        </div>
      </Card>

      {/* Main body split */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        {/* LEFT (60%) */}
        <div className="lg:col-span-3">
          <Card hover={false} padded={false}>
            <div className="px-4 pt-4 pb-2">
              <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>Project Breakdown</div>
              <div className="text-xs mt-1" style={{ color: 'var(--ui-text-muted)' }}>
                {projectId === 'all' ? 'All projects' : 'Filtered by project'}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--ui-surface2)' }}>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Project</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Planned</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Logged</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>%</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Active tasks</th>
                    <th className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }} />
                  </tr>
                </thead>
                <tbody>
                  {projectBreakdown.map((r) => (
                    <tr key={r.projectId}>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>{r.projectName}</div>
                      </td>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>{formatHours(r.planned)}</td>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>{formatHours(r.logged)}</td>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>{formatPct(r.pct)}</td>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>{r.activeTasks}</td>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <Link
                          to={`/projects/${encodeURIComponent(String(r.projectId))}`}
                          className="text-xs underline"
                          style={{ color: 'var(--ui-primary)' }}
                        >
                          Open project
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {projectBreakdown.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-sm" style={{ color: 'var(--ui-text-muted)' }}>
                        No project breakdown available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* RIGHT (40%) */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          <Card hover={false} padded={false}>
            <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>Tasks & Calendar Snapshot</div>
                <div className="text-xs mt-1" style={{ color: 'var(--ui-text-muted)' }}>Active tasks</div>
              </div>
              <button
                type="button"
                onClick={() => navigate(openTasksContextUrl)}
                className="text-xs rounded px-2 py-1 border"
                style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
              >
                Open
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--ui-surface2)' }}>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Task</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Due</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Status</th>
                    <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Planned/Logged</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.slice(0, 6).map((t) => (
                    <tr key={t.id}>
                      <td className="px-4 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        {(() => {
                          const to = buildProjectTasksUrl({
                            projectId: t.projectId,
                            taskId: t.taskId,
                            personId,
                            period,
                          });
                          return to ? (
                            <Link
                              to={to}
                              className="text-xs underline"
                              style={{ color: 'var(--ui-primary)' }}
                            >
                              {t.title}
                            </Link>
                          ) : (
                            <span className="text-xs" style={{ color: 'var(--ui-text)' }}>{t.title}</span>
                          );
                        })()}
                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--ui-text-muted)' }}>{t.projectName}</div>
                      </td>
                      <td className="px-4 py-2 text-xs" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>{t.dueDate}</td>
                      <td
                        className="px-4 py-2 text-xs"
                        style={{
                          borderBottom: '1px solid var(--ui-border)',
                          color: isOverdueDate(t.dueDate, t.status) ? 'var(--ui-danger)' : 'var(--ui-text)',
                        }}
                      >
                        {isOverdueDate(t.dueDate, t.status) ? 'overdue' : (t.status || '—')}
                      </td>
                      <td className="px-4 py-2 text-xs" style={{ borderBottom: '1px solid var(--ui-border)', color: 'var(--ui-text)' }}>
                        {formatHours(t.plannedHours)} / {formatHours(t.loggedHours)}
                      </td>
                    </tr>
                  ))}
                  {tasks.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-sm" style={{ color: 'var(--ui-text-muted)' }}>
                        No tasks.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 border-t" style={{ borderTopColor: 'var(--ui-border)' }}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold" style={{ color: 'var(--ui-text)' }}>Overdue tasks</div>
                <button
                  type="button"
                  onClick={() => {
                    if (overdueProjectIds.size === 1) {
                      const onlyPid = Array.from(overdueProjectIds)[0];
                      const to = buildProjectTasksUrl({ projectId: onlyPid, personId, period, status: 'overdue' });
                      if (to) navigate(to);
                      else navigate('/projects');
                      return;
                    }

                    // Multiple projects: keep /tasks?... (App has a /tasks shim that redirects into /projects).
                    const params = new URLSearchParams();
                    params.set('assignee', String(person.id));
                    params.set('status', 'overdue');
                    params.set('period', String(period));
                    params.set('person', String(personId));
                    navigate(`/tasks?${params.toString()}`);
                  }}
                  className="text-xs underline"
                  style={{ color: 'var(--ui-primary)' }}
                >
                  View overdue
                </button>
              </div>
              <div className="mt-2 flex flex-col gap-1">
                {overdueTasks.slice(0, 4).map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center justify-between gap-2 rounded px-2 py-1"
                    style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}
                  >
                    <div className="min-w-0">
                      <div className="text-xs truncate" style={{ color: 'var(--ui-text)' }}>{t.title}</div>
                      <div className="text-[11px]" style={{ color: 'var(--ui-text-muted)' }}>{t.projectName} • due {t.dueDate}</div>
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--ui-danger)' }}>overdue</div>
                  </div>
                ))}
                {overdueTasks.length === 0 && (
                  <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>No overdue tasks.</div>
                )}
              </div>
            </div>

          </Card>
        </div>
      </div>
    </div>
  );
}
